import { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useModalBack } from "@/hooks/useModalBack";
import {
  Phone,
  Cake,
  Calendar,
  HeartPulse,
  IdCard,
  BadgeCheck,
  FileText,
  Pencil,
  Landmark,
  Eye,
  EyeOff,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const formatDate = (s) => {
  if (!s) return "";
  const iso = s.length === 10 ? `${s}T00:00:00` : s;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const ageFrom = (dob) => {
  if (!dob) return null;
  const iso = dob.length === 10 ? `${dob}T00:00:00` : dob;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const beforeBirthday =
    now.getMonth() < d.getMonth() ||
    (now.getMonth() === d.getMonth() && now.getDate() < d.getDate());
  if (beforeBirthday) years -= 1;
  return years >= 0 ? years : null;
};

const tenure = (s) => {
  if (!s) return "";
  const iso = s.length === 10 ? `${s}T00:00:00` : s;
  const start = new Date(iso);
  if (isNaN(start.getTime())) return "";
  const now = new Date();
  if (start > now) return "future hire";
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();
  if (days < 0) {
    months -= 1;
    const prevMonthLastDay = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
    days += prevMonthLastDay;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  const parts = [];
  if (years > 0) parts.push(`${years} yr${years === 1 ? "" : "s"}`);
  if (months > 0) parts.push(`${months} mo`);
  if (days > 0 && years === 0) parts.push(`${days} day${days === 1 ? "" : "s"}`);
  if (parts.length === 0) return "just hired";
  return parts.join(", ");
};

const Field = ({ label, value, mono = false, testid, revealedValue, keepEmpty = false }) => {
  const shown = revealedValue !== undefined && revealedValue !== null && revealedValue !== "";
  const display = shown ? revealedValue : value;
  const isEmpty = display == null || display === "";
  if (isEmpty && !keepEmpty) return null;
  return (
    <div className="min-w-0" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{label}</p>
      <p className={`mt-1 break-words text-sm ${shown ? "text-emerald-300" : isEmpty ? "text-white/30" : "text-white/90"} ${mono ? "font-mono" : ""}`}>{isEmpty ? "—" : display}</p>
    </div>
  );
};

const Section = ({ icon: Icon, title, children, action }) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
    <div className="mb-3 flex items-center justify-between gap-2 text-white/70">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#8fa4f0]" />
        <h3 className="font-mono text-[11px] uppercase tracking-[0.22em]">{title}</h3>
      </div>
      {action}
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
  </div>
);

const RevealButton = ({ shown, busy, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    data-testid={testid}
    aria-label={shown ? "Hide sensitive" : "Reveal sensitive"}
    title={shown ? "Hide" : "Reveal"}
    className="flex items-center gap-1 rounded border border-white/15 bg-white/5 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-white/70 hover:border-white/25 hover:text-white"
  >
    {shown ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
    {busy ? "…" : (shown ? "Hide" : "Reveal")}
  </button>
);

const statusStyle = (s) =>
  s === "Active"
    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
    : "bg-white/10 text-white/50 border-white/20";

export default function EmployeeDetailsDialog({ employee, onOpenChange, onEdit }) {
  const open = !!employee;
  useModalBack(open, () => onOpenChange?.(false));
  const e = employee || {};
  const { user } = useAuth();
  const canReveal =
    user?.role === "superadmin" ||
    user?.role === "owner" ||
    (e.employee_id && user?.linked_employee_id === e.employee_id);

  // Plaintext cache + per-section reveal flags.
  const [sensitive, setSensitive] = useState(null);
  const [busy, setBusy] = useState("");
  const [showGov, setShowGov] = useState(false);
  const [showBank, setShowBank] = useState(false);

  const fetchSensitive = useCallback(async () => {
    if (sensitive) return sensitive;
    setBusy("loading");
    try {
      const { data } = await api.get(`/employees/${e.employee_id}/sensitive`);
      setSensitive(data || {});
      return data || {};
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cannot reveal");
      return null;
    } finally {
      setBusy("");
    }
  }, [sensitive, e.employee_id]);

  const toggleGov = useCallback(async () => {
    if (showGov) { setShowGov(false); return; }
    if (!sensitive) { const data = await fetchSensitive(); if (!data) return; }
    setShowGov(true);
  }, [showGov, sensitive, fetchSensitive]);
  const toggleBank = useCallback(async () => {
    if (showBank) { setShowBank(false); return; }
    if (!sensitive) { const data = await fetchSensitive(); if (!data) return; }
    setShowBank(true);
  }, [showBank, sensitive, fetchSensitive]);

  const address = [e.address_street, e.address_city, e.address_postal].filter(Boolean).join(", ");
  const emergency = e.emergency_contact_name
    ? [
        e.emergency_contact_name,
        e.emergency_contact_relationship ? `(${e.emergency_contact_relationship})` : "",
      ]
        .filter(Boolean)
        .join(" ")
    : "";
  const emergency2 = e.emergency_contact_2_name
    ? [
        e.emergency_contact_2_name,
        e.emergency_contact_2_relationship ? `(${e.emergency_contact_2_relationship})` : "",
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  const hiredLine = e.hired_at
    ? `${formatDate(e.hired_at)}${tenure(e.hired_at) ? `  (${tenure(e.hired_at)})` : ""}`
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#0b0b0f] text-white sm:max-w-3xl"
        data-testid="employee-details-dialog"
      >
        <DialogHeader>
          <div className="flex items-start gap-4">
            {e.photo ? (
              <img
                src={e.photo}
                alt={e.name || ""}
                data-testid="details-photo"
                className="h-24 w-24 shrink-0 rounded-lg object-cover ring-1 ring-white/10"
              />
            ) : (
              <div
                data-testid="details-photo-placeholder"
                className="flex h-24 w-24 shrink-0 items-center justify-center rounded-lg border border-dashed border-white/15 bg-white/[0.03] font-mono text-[10px] uppercase tracking-wider text-white/40"
              >
                No photo
              </div>
            )}
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate text-2xl">{e.name}</DialogTitle>
              <DialogDescription className="text-white/60">
                {[e.job_title, e.trade].filter(Boolean).join(" · ") || "—"}
                {e.status ? (
                  <span
                    className={`ml-3 inline-block border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${statusStyle(
                      e.status,
                    )}`}
                  >
                    {e.status}
                  </span>
                ) : null}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Section icon={Phone} title="Contact">
            <Field label="Phone" value={e.phone} testid="details-phone" />
            <Field label="Email" value={e.email} testid="details-email" />
            <div className="sm:col-span-2">
              <Field label="Address" value={address} testid="details-address" />
            </div>
          </Section>

          <Section icon={Calendar} title="Employment">
            <Field label="Job title" value={e.job_title} testid="details-job-title" />
            <Field label="Trade / role" value={e.trade} testid="details-trade" />
            <Field
              label="Employment type"
              value={(e.employment_type || "full_time") === "part_time" ? "Part-time" : "Full-time"}
              testid="details-employment-type"
            />
            <Field label="Employed from" value={hiredLine} testid="details-hired-at" />
            <Field
              label={e.pay_type === "salary" ? "Weekly salary" : "Hourly rate"}
              value={e.pay_type === "salary" ? `${money(e.weekly_salary)}/wk` : money(e.hourly_rate)}
              mono
              testid="details-pay"
            />
          </Section>

          <Section icon={Cake} title="Personal">
            <Field
              label="Date of birth"
              value={
                e.date_of_birth
                  ? `${formatDate(e.date_of_birth)}${
                      ageFrom(e.date_of_birth) !== null ? `  (${ageFrom(e.date_of_birth)} yrs old)` : ""
                    }`
                  : ""
              }
              testid="details-dob"
            />
            <Field label="Shirt / uniform size" value={e.shirt_size} testid="details-shirt" />
          </Section>

          <Section icon={HeartPulse} title="Emergency contact 1">
            <Field label="Name" value={emergency} testid="details-ec-name" />
            <Field label="Phone" value={e.emergency_contact_phone} testid="details-ec-phone" />
            <div className="sm:col-span-2">
              <Field label="Allergies" value={e.allergies} testid="details-allergies" />
            </div>
          </Section>

          {(emergency2 || e.emergency_contact_2_phone) && (
            <Section icon={HeartPulse} title="Emergency contact 2">
              <Field label="Name" value={emergency2} testid="details-ec2-name" />
              <Field label="Phone" value={e.emergency_contact_2_phone} testid="details-ec2-phone" />
            </Section>
          )}

          <Section
            icon={IdCard}
            title="Government & License"
            action={canReveal && (
              <RevealButton
                shown={showGov}
                busy={busy === "loading" && !showGov}
                onClick={toggleGov}
                testid="details-reveal-gov"
              />
            )}
          >
            <Field label="SIN" value={e.sin} mono testid="details-sin" revealedValue={showGov ? sensitive?.sin : undefined} keepEmpty />
            <Field label="Driver's license #" value={e.license_number} mono testid="details-license" revealedValue={showGov ? sensitive?.license_number : undefined} keepEmpty />
            <Field
              label="License expiry"
              value={formatDate(e.license_expiry)}
              testid="details-license-expiry"
              keepEmpty
            />
          </Section>

          <Section
            icon={Landmark}
            title="Direct deposit"
            action={canReveal && (
              <RevealButton
                shown={showBank}
                busy={busy === "loading" && !showBank}
                onClick={toggleBank}
                testid="details-reveal-bank"
              />
            )}
          >
            <Field label="Transit #" value={e.bank_transit} mono testid="details-bank-transit" revealedValue={showBank ? sensitive?.bank_transit : undefined} keepEmpty />
            <Field label="Institution #" value={e.bank_institution} mono testid="details-bank-institution" revealedValue={showBank ? sensitive?.bank_institution : undefined} keepEmpty />
            <div className="sm:col-span-2">
              <Field label="Account #" value={e.bank_account} mono testid="details-bank-account" revealedValue={showBank ? sensitive?.bank_account : undefined} keepEmpty />
            </div>
          </Section>

          {Array.isArray(e.certifications) && e.certifications.length > 0 && (
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
              <div className="mb-3 flex items-center gap-2 text-white/70">
                <BadgeCheck className="h-4 w-4 text-[#8fa4f0]" />
                <h3 className="font-mono text-[11px] uppercase tracking-[0.22em]">
                  Certifications / Tickets
                </h3>
              </div>
              <ul className="space-y-1.5" data-testid="details-certifications">
                {e.certifications.map((c, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between rounded border border-white/10 bg-white/[0.02] px-3 py-1.5 text-sm"
                  >
                    <span className="text-white/90">{c?.name || "—"}</span>
                    <span className="font-mono text-xs text-white/50">
                      {c?.expiry ? `exp ${formatDate(c.expiry)}` : "no expiry"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {e.notes && (
            <Section icon={FileText} title="Notes">
              <div className="sm:col-span-2">
                <p className="whitespace-pre-wrap text-sm text-white/80" data-testid="details-notes">
                  {e.notes}
                </p>
              </div>
            </Section>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/20 bg-transparent text-white hover:bg-white/10"
            data-testid="details-close"
          >
            Close
          </Button>
          <Button
            onClick={() => onEdit?.(e)}
            className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85"
            data-testid="details-edit-btn"
          >
            <Pencil className="mr-1 h-4 w-4" /> Edit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
